import React, { memo } from 'react';
import type { DocumentInfo } from '../types';

interface DocumentListSectionProps {
  documentUrls: {[key: string]: DocumentInfo};
  isEditing: boolean;
  downloading: string | null;
  onFileChange: (e: React.ChangeEvent<HTMLInputElement>, type: string) => void;
  onDeleteFile: (type: string) => void;
  onDownload: (storagePath: string, type: string, applicationName?: string) => Promise<void>;
  applicationName?: string;
  newDocuments?: {[key: string]: DocumentInfo};
  fileError?: string | null;
}

const getDocumentDisplayName = (docType: string) => {
  switch (docType) {
    case 'identification': return '신분증 사본';
    case 'employment': return '재직증명서/사업자등록증';
    case 'other': return '기타 서류';
    default: return '문서';
  }
};

const DocumentListSectionComponent: React.FC<DocumentListSectionProps> = ({
  documentUrls,
  isEditing,
  downloading,
  onFileChange,
  onDeleteFile,
  onDownload,
  applicationName,
  newDocuments = {},
  fileError
}) => {
  return (
    <div className="p-4 md:p-8">
      <h2 className="text-[#1e2c49] text-lg md:text-xl font-bold font-['Pretendard'] leading-9 mb-3 md:mb-4">
        제출 서류
      </h2>
      <div className="w-full md:w-[calc(100%-64px)] h-px outline outline-1 outline-offset-[-0.50px] outline-[#e5e8eb] mb-6 md:mb-8"></div>
      
      {/* 파일 에러 메시지 인라인 표시 */}
      {fileError && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
          {fileError}
        </div>
      )}
      
      <div className="space-y-3 md:space-y-4">
        {Object.entries(documentUrls).map(([docType, docInfo]) => {
          const newDoc = newDocuments[docType];
          const selectedFile = newDoc?.file;
          const isFileChanged = Boolean(selectedFile);
          
          return (
            <div key={docType} className="flex flex-col gap-2 sm:gap-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-4">
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 md:w-6 md:h-6 relative flex-shrink-0">
                    <div className="w-[14.38px] h-[16.26px] md:w-[17.25px] md:h-[19.51px] left-[2.5px] top-[1.88px] md:left-[3px] md:top-[2.25px] absolute bg-[#2e97ee]"></div>
                  </div>
                  <span className="text-[#1e2c49] text-sm md:text-base font-medium">
                    {getDocumentDisplayName(docType)}
                    {(docType === 'identification' || docType === 'employment') && (
                      <span className="ml-1 md:ml-2 text-xs md:text-sm text-red-500">(필수)</span>
                    )}
                  </span>
                </div>
                <div className="flex gap-2 flex-wrap sm:flex-nowrap">
                  {isEditing ? (
                    <>
                      <input
                        type="file"
                        onChange={(e) => onFileChange(e, docType)}
                        className="hidden"
                        id={`file-${docType}`}
                        accept=".pdf,.jpg,.jpeg,.png"
                      />
                      <label
                        htmlFor={`file-${docType}`}
                        className="px-3 md:px-4 py-1.5 md:py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors cursor-pointer text-sm md:text-base whitespace-nowrap"
                      >
                        파일 변경
                      </label>
                      {docType !== 'identification' && docType !== 'employment' && (
                        <button
                          onClick={() => onDeleteFile(docType)}
                          className="px-3 md:px-4 py-1.5 md:py-2 bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors text-sm md:text-base whitespace-nowrap"
                        >
                          삭제
                        </button>
                      )}
                    </>
                  ) : (
                    <button
                      onClick={() => onDownload(docInfo.storagePath, docType, applicationName)}
                      disabled={downloading === docType}
                      className="px-3 md:px-4 py-1.5 md:py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors disabled:bg-gray-400 text-sm md:text-base whitespace-nowrap"
                    >
                      {downloading === docType ? '다운로드 중...' : '다운로드'}
                    </button>
                  )}
                </div>
              </div>
              
              {/* 선택된 파일 정보 표시 */}
              {isEditing && isFileChanged && selectedFile && (
                <div className="ml-7 sm:ml-8 p-2 bg-blue-50 border border-blue-200 rounded text-sm">
                  <div className="flex items-center gap-2">
                    <span className="text-blue-700 font-medium">선택된 파일:</span>
                    <span className="text-blue-600">{selectedFile.name}</span>
                    <span className="text-blue-500 text-xs">
                      ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                    </span>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const DocumentListSection = memo(DocumentListSectionComponent);
